import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { taskService } from '../../services/taskService';
import { navigate } from '../../services/navigationService';

const TaskManagement = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [taskStats, setTaskStats] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignedTo: '',
    taskType: ''
  });

  // New task form data
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    taskType: 'treatment_application',
    priority: 'medium',
    dueDate: '',
    location: '',
    instructions: '',
    estimatedDuration: '',
    assignedTo: '',
    assignmentNotes: ''
  });

  // Demo data for non-authenticated users
  const demoTasks = [
    {
      id: 'demo-1',
      title: 'Spider Mite Treatment - Greenhouse A',
      description: 'Apply Neoseiulus californicus predatory mites for spider mite control in Greenhouse A sections 1-4',
      task_type: 'biological_control',
      priority: 'high',
      status: 'assigned',
      due_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)?.toISOString(), // 2 days from now
      location: 'Greenhouse A - Sections 1-4',
      instructions: 'Release sachets evenly throughout affected areas. Focus on hotspots identified during scouting. Monitor establishment after 48 hours.',
      estimated_duration: 45,
      assigned_user: { full_name: 'Demo User', email: 'demo@example.com' }
    },
    {
      id: 'demo-2',
      title: 'Weekly Scout - Flowering Rooms',
      description: 'Conduct weekly IPM scouting in all flowering rooms to identify pest and disease pressure',
      task_type: 'monitoring',
      priority: 'medium',
      status: 'pending',
      due_date: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString(), // 1 day from now
      location: 'All Flowering Rooms',
      instructions: 'Check 10% of plants per room. Document any pests, diseases, or beneficial insects found. Take photos of issues.',
      estimated_duration: 120,
      assigned_user: null
    },
    {
      id: 'demo-3',
      title: 'BT Application - Room C',
      description: 'Apply Bacillus thuringiensis for caterpillar control in Room C',
      task_type: 'treatment_application',
      priority: 'critical',
      status: 'completed',
      due_date: new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString(), // 1 day ago
      completed_date: new Date(Date.now() - 2 * 60 * 60 * 1000)?.toISOString(), // 2 hours ago
      location: 'Room C - Flowering',
      instructions: 'Mix BT at 1ml per liter. Apply during lights off period. Focus on plants showing caterpillar damage.',
      estimated_duration: 60,
      assigned_user: { full_name: 'Demo User', email: 'demo@example.com' }
    }
  ];

  const demoStats = {
    total: 3,
    completed: 1,
    pending: 1,
    overdue: 0
  };

  const demoTeamMembers = [
    { user_profiles: { id: 'demo-user-1', full_name: 'Demo User', email: 'demo@example.com', role: 'supervisor' } },
    { user_profiles: { id: 'demo-user-2', full_name: 'Sample Tech', email: 'tech@example.com', role: 'employee' } }
  ];

  const taskTypeOptions = [
    { value: 'treatment_application', label: 'Treatment Application' },
    { value: 'biological_control', label: 'Biological Control' },
    { value: 'monitoring', label: 'Monitoring' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'pruning', label: 'Pruning' },
    { value: 'defoliation', label: 'Defoliation' },
    { value: 'cultivation', label: 'Cultivation' },
    { value: 'harvest', label: 'Harvest' },
    { value: 'transplanting', label: 'Transplanting' },
    { value: 'watering', label: 'Watering' },
    { value: 'nutrition', label: 'Nutrition' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    loadData();
  }, [filters, user]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      if (!user) {
        // Show demo data for non-authenticated users
        let filteredTasks = [...demoTasks];
        
        // Apply filters to demo data
        if (filters?.status) {
          filteredTasks = filteredTasks?.filter(task => task?.status === filters?.status);
        }
        if (filters?.priority) {
          filteredTasks = filteredTasks?.filter(task => task?.priority === filters?.priority);
        }
        if (filters?.taskType) {
          filteredTasks = filteredTasks?.filter(task => task?.task_type === filters?.taskType);
        }
        
        setTasks(filteredTasks);
        setTaskStats(demoStats);
        setTeamMembers(demoTeamMembers);
        setLoading(false);
        return;
      }

      // Load real data for authenticated users
      const [tasksData, statsData, teamData] = await Promise.all([
        taskService?.getTasks(filters),
        taskService?.getTaskStats(),
        taskService?.getTeamMembers()
      ]);

      setTasks(tasksData || []);
      setTaskStats(statsData || { total: 0, completed: 0, pending: 0, overdue: 0 });
      setTeamMembers(teamData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      if (!user) {
        // If not authenticated, still show demo data but with a notice
        setTasks(demoTasks);
        setTaskStats(demoStats);
        setTeamMembers(demoTeamMembers);
        setError('Showing example tasks. Sign in to manage your real tasks.');
      } else {
        setError('Failed to load task data. Please try again.');
        setTasks([]);
        setTaskStats({ total: 0, completed: 0, pending: 0, overdue: 0 });
        setTeamMembers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!user) {
      setError('Please sign in to create tasks');
      return;
    }

    try {
      if (!newTask?.title || !newTask?.description) {
        setError('Title and description are required');
        return;
      }

      await taskService?.createTask(newTask);
      setShowCreateForm(false);
      setNewTask({
        title: '',
        description: '',
        taskType: 'treatment_application',
        priority: 'medium',
        dueDate: '',
        location: '',
        instructions: '',
        estimatedDuration: '',
        assignedTo: '',
        assignmentNotes: ''
      });
      await loadData();
    } catch (err) {
      setError('Failed to create task: ' + (err?.message || 'Unknown error'));
      console.error('Error creating task:', err);
    }
  };

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    if (!user) {
      setError('Please sign in to update tasks');
      return;
    }

    try {
      await taskService?.updateTask(taskId, { status: newStatus });
      await loadData();
    } catch (err) {
      setError('Failed to update task status: ' + (err?.message || 'Unknown error'));
      console.error('Error updating task:', err);
    }
  };

  const handleCompleteTask = async (taskId) => {
    if (!user) {
      setError('Please sign in to complete tasks');
      return;
    }

    try {
      await taskService?.completeTask(taskId);
      await loadData();
    } catch (err) {
      setError('Failed to complete task: ' + (err?.message || 'Unknown error'));
      console.error('Error completing task:', err);
    }
  };

  const formatDueDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const now = new Date();
    const isOverdue = date < now;
    
    return (
      <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
        {date?.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
        {isOverdue && ' (Overdue)'}
      </span>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
            <div className="flex items-center justify-center">
              <Icon name="Loader2" size={24} className="animate-spin text-green-600 mr-2" />
              <span className="text-gray-600">Loading tasks...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header with Actions */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Task Management</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-2">Organize and track team tasks</p>
            </div>
            <button className="px-4 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors touch-manipulation text-sm sm:text-base w-full sm:w-auto">
              Create New Task
            </button>
          </div>
        </div>

        {/* Filters - Responsive */}
        <div className="mb-6 bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              placeholder="Filter by status"
            />
            <Select
              options={priorityOptions?.map(p => ({ ...p, value: p?.value, label: p?.label }))}
              value={filters?.priority}
              onChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}
              placeholder="Filter by priority"
            />
            <Select
              options={[
                { value: '', label: 'All Assignees' },
                ...teamMembers?.map(member => ({
                  value: member?.user_profiles?.id,
                  label: member?.user_profiles?.full_name
                }))
              ]}
              value={filters?.assignedTo}
              onChange={(value) => setFilters(prev => ({ ...prev, assignedTo: value }))}
              placeholder="Filter by assignee"
            />
            <Select
              options={[
                { value: '', label: 'All Task Types' },
                ...taskTypeOptions
              ]}
              value={filters?.taskType}
              onChange={(value) => setFilters(prev => ({ ...prev, taskType: value }))}
              placeholder="Filter by type"
            />
          </div>
        </div>

        {/* Task List - Touch-friendly */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 sm:p-6">
            {tasks?.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="CheckSquare" size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
                <p className="text-gray-600 mb-4">
                  {user ? 'Get started by creating your first task' : 'Try adjusting your filters to see more example tasks'}
                </p>
                {user ? (
                  <Button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Create Task
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/login')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Sign In to Create Tasks
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {tasks?.map((task) => (
                  <div key={task?.id} className="p-6 hover:bg-gray-50">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
                      
                      {/* Task Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start space-x-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-medium text-gray-900 mb-1">
                              {task?.title}
                            </h3>
                            <p className="text-gray-600 mb-3 leading-relaxed">
                              {task?.description}
                            </p>
                            
                            {/* Task Meta */}
                            <div className="flex flex-wrap items-center gap-4 text-sm">
                              <div className="flex items-center space-x-2">
                                <Icon name="MapPin" size={14} className="text-gray-400" />
                                <span className="text-gray-600">{task?.location || 'No location'}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Icon name="Clock" size={14} className="text-gray-400" />
                                {formatDueDate(task?.due_date)}
                              </div>
                              {task?.assigned_user && (
                                <div className="flex items-center space-x-2">
                                  <Icon name="User" size={14} className="text-gray-400" />
                                  <span className="text-gray-600">{task?.assigned_user?.full_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Task Actions */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                        
                        {/* Status & Priority Badges */}
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task?.status)}`}>
                            {task?.status?.replace('_', ' ')}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task?.priority)}`}>
                            {task?.priority}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                          {user && task?.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCompleteTask(task?.id)}
                              className="text-green-600 border-green-600 hover:bg-green-50"
                            >
                              <Icon name="CheckCircle" size={14} className="mr-1" />
                              Complete
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedTask(task)}
                          >
                            <Icon name="Eye" size={14} className="mr-1" />
                            View
                          </Button>
                          {!user && (
                            <Button
                              size="sm"
                              onClick={() => navigate('/login')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Sign In
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Task Modal */}
      {showCreateForm && user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Create New Task</h2>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Title *
                  </label>
                  <Input
                    value={newTask?.title}
                    onChange={(e) => setNewTask(prev => ({ ...prev, title: e?.target?.value }))}
                    placeholder="Enter task title"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newTask?.description}
                    onChange={(e) => setNewTask(prev => ({ ...prev, description: e?.target?.value }))}
                    placeholder="Enter task description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Task Type
                  </label>
                  <Select
                    options={taskTypeOptions}
                    value={newTask?.taskType}
                    onChange={(value) => setNewTask(prev => ({ ...prev, taskType: value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <Select
                    options={priorityOptions}
                    value={newTask?.priority}
                    onChange={(value) => setNewTask(prev => ({ ...prev, priority: value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Date
                  </label>
                  <Input
                    type="datetime-local"
                    value={newTask?.dueDate}
                    onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e?.target?.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <Input
                    value={newTask?.location}
                    onChange={(e) => setNewTask(prev => ({ ...prev, location: e?.target?.value }))}
                    placeholder="Task location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign To
                  </label>
                  <Select
                    options={[
                      { value: '', label: 'Unassigned' },
                      ...teamMembers?.map(member => ({
                        value: member?.user_profiles?.id,
                        label: member?.user_profiles?.full_name
                      }))
                    ]}
                    value={newTask?.assignedTo}
                    onChange={(value) => setNewTask(prev => ({ ...prev, assignedTo: value }))}
                    placeholder="Select team member"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Duration (minutes)
                  </label>
                  <Input
                    type="number"
                    value={newTask?.estimatedDuration}
                    onChange={(e) => setNewTask(prev => ({ ...prev, estimatedDuration: e?.target?.value }))}
                    placeholder="Duration in minutes"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions
                </label>
                <textarea
                  value={newTask?.instructions}
                  onChange={(e) => setNewTask(prev => ({ ...prev, instructions: e?.target?.value }))}
                  placeholder="Detailed instructions for completing this task"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTask}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Create Task
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManagement;